import React, { useState, useEffect, useMemo, useCallback } from "react";
import Button from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import Dropdown from "../../../components/Dropdown";
import Checkbox from "../../../components/Checkbox";
import MultiSelect from "../../../components/MultiSelect";
import Dialog from "../../../components/Dialog";
import {
  createSubscriptionPlan,
  updateSubscriptionPlan,
  getCourses,
} from "../../../api";
import { ALPHA_NUMERIC_SPACE, NUMBERS_ONLY } from "../../../config/regex";
import Swal from "sweetalert2";

/** Growth plans (name contains whole word "growth", case-insensitive) have course limits. */
const maxFreeAccessCourses = (name, billingCycle) => {
  if (!/\bgrowth\b/i.test(String(name || "").trim())) return null;
  return billingCycle === "annually" ? 7 : 3;
};

const emptyForm = () => ({
  name: "",
  price: "",
  billingCycle: "monthly",
  tag: "",
  description: "",
  featuresText: "",
  sortOrder: 0,
  slug: "",
  isAllAccess: false,
});

const AddPlanModal = ({ open, onClose, onSuccess, editPlan }) => {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [courseOptions, setCourseOptions] = useState([]);
  const [coursesLoadError, setCoursesLoadError] = useState("");
  const [selectedFreeCourses, setSelectedFreeCourses] = useState([]);

  const isEdit = Boolean(editPlan?._id);

  const loadCourses = useCallback(async () => {
    setCoursesLoadError("");
    try {
      const list = await getCourses();
      setCourseOptions(Array.isArray(list) ? list : []);
    } catch (err) {
      setCourseOptions([]);
      setCoursesLoadError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not load courses. Check API URL and login.",
      );
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadCourses();
  }, [open, loadCourses]);

  useEffect(() => {
    if (!open) return;
    setError("");
    setErrors({});
    setTouched({});
    if (editPlan) {
      setForm({
        name: editPlan.name || "",
        price: editPlan.price || "",
        billingCycle: editPlan.billingCycle || "monthly",
        tag: editPlan.tag || "",
        description: editPlan.description || "",
        featuresText: Array.isArray(editPlan.features)
          ? editPlan.features.join("\n")
          : "",
        sortOrder: editPlan.sortOrder ?? 0,
        slug: editPlan.slug || "",
        isAllAccess: !!editPlan.isAllAccess,
      });
      setSelectedFreeCourses([]);
    } else {
      setForm(emptyForm());
      setSelectedFreeCourses([]);
    }
  }, [open, editPlan]);

  useEffect(() => {
    if (!open) return;
    if (!editPlan) {
      return;
    }
    if (!courseOptions.length) return;
    const ids = editPlan.freeAccessCourseIds || [];
    if (!ids.length) {
      setSelectedFreeCourses([]);
      return;
    }
    const idSet = new Set(ids.map(String));
    setSelectedFreeCourses(
      courseOptions.filter((c) => idSet.has(String(c._id || c.id))),
    );
  }, [open, editPlan, courseOptions]);

  const validateField = (name, value) => {
    if (name === "isAllAccess") return "";
    if (!String(value || "").trim() && name !== "sortOrder") {
      return "This field is required";
    }

    switch (name) {
      case "name":
      case "tag":
      case "slug":
        if (!ALPHA_NUMERIC_SPACE.test(value)) {
          return "Only letters, numbers, and basic punctuation allowed";
        }
        if (value.length < 2) return "Must be at least 2 characters";
        break;
      case "price":
        if (!NUMBERS_ONLY.test(value)) {
          return "Please enter numbers only";
        }
        break;
      case "description":
      case "featuresText":
        if (value.length < 5) return "Must be at least 5 characters";
        break;
      case "sortOrder":
        if (value !== "" && isNaN(Number(value))) return "Must be a number";
        break;
      default:
        break;
    }
    return "";
  };

  const isFormInvalid = useMemo(() => {
    const requiredFields = ["name", "price", "tag", "description", "featuresText", "slug"];
    for (const f of requiredFields) {
      if (validateField(f, form[f])) return true;
    }
    return false;
  }, [form]);

  const isChanged = useMemo(() => {
    if (!isEdit || !editPlan) return true;
    
    // Compare basic fields
    const hasBasicChange = 
      form.name !== (editPlan.name || "") ||
      form.price !== (editPlan.price || "") ||
      form.billingCycle !== (editPlan.billingCycle || "monthly") ||
      form.tag !== (editPlan.tag || "") ||
      form.description !== (editPlan.description || "") ||
      form.slug !== (editPlan.slug || "") ||
      Number(form.sortOrder) !== (editPlan.sortOrder ?? 0) ||
      form.isAllAccess !== !!editPlan.isAllAccess;

    if (hasBasicChange) return true;

    // Compare features
    const currentFeatures = form.featuresText.split("\n").map(s => s.trim()).filter(Boolean).join("\n");
    const originalFeatures = Array.isArray(editPlan.features) ? editPlan.features.join("\n") : "";
    if (currentFeatures !== originalFeatures) return true;

    // Compare selected courses
    const currentCourseIds = new Set(selectedFreeCourses.map(c => String(c._id || c.id)));
    const originalCourseIds = new Set((editPlan.freeAccessCourseIds || []).map(String));
    
    if (currentCourseIds.size !== originalCourseIds.size) return true;
    for (const id of currentCourseIds) {
      if (!originalCourseIds.has(id)) return true;
    }

    return false;
  }, [form, isEdit, editPlan, selectedFreeCourses]);

  const handleChange = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setTouched((t) => ({ ...t, [name]: true }));
    const err = validateField(name, value);
    setErrors((e) => ({ ...e, [name]: err }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (isFormInvalid) {
      setError("Please fix the errors before saving.");
      return;
    }

    const max = maxFreeAccessCourses(form.name, form.billingCycle);
    if (max != null && selectedFreeCourses.length > max) {
      setError(
        `Growth ${form.billingCycle === "annually" ? "annually" : "monthly"} plan allows at most ${max} free-access courses.`,
      );
      return;
    }

    const features = form.featuresText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const payload = {
      name: form.name.trim(),
      price: form.price.trim(),
      billingCycle: form.billingCycle === "annually" ? "annually" : "monthly",
      planType: form.billingCycle === "annually" ? "annually" : "monthly",
      tag: (form.tag || "").trim(),
      description: (form.description || "").trim(),
      features,
      sortOrder: Number(form.sortOrder) || 0,
      slug: (form.slug || "").trim(),
      freeAccessCourses: form.isAllAccess
        ? []
        : selectedFreeCourses.map((c) => c._id || c.id),
      isAllAccess: !!form.isAllAccess,
    };
    try {
      setSaving(true);
      if (isEdit) {
        await updateSubscriptionPlan(editPlan._id, payload);
      } else {
        await createSubscriptionPlan(payload);
      }
      setForm(emptyForm());
      setErrors({});
      setTouched({});
      setSelectedFreeCourses([]);
      onSuccess?.();
      onClose();
      
      Swal.fire({
        icon: "success",
        title: "Success",
        text: `Plan ${isEdit ? "updated" : "created"} successfully`,
        confirmButtonColor: "#38915c",
      });
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to save plan",
      );
    } finally {
      setSaving(false);
    }
  };

  const onCoursesChange = (_event, newValue) => {
    const max = maxFreeAccessCourses(form.name, form.billingCycle);
    if (max != null && newValue.length > max) {
      setError(`You can select at most ${max} courses for this Growth plan.`);
      setSelectedFreeCourses(newValue.slice(0, max));
      return;
    }
    setError("");
    setSelectedFreeCourses(newValue);
  };

  const footer = (
    <>
      <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
        Cancel
      </Button>
      <Button
        variant="primary"
        size="sm"
        onClick={(ev) => handleSubmit(ev)}
        disabled={saving || isFormInvalid || (isEdit && !isChanged)}
      >
        {saving ? "Saving..." : isEdit ? "Update Plan" : "Add Plan"}
      </Button>
    </>
  );

  const freeAccessHelper = {
    max: maxFreeAccessCourses(form.name, form.billingCycle),
    hint: maxFreeAccessCourses(form.name, form.billingCycle) != null
        ? `Note : Growth plan (${form.billingCycle === "annually" ? "annual" : "monthly"}): up to ${maxFreeAccessCourses(form.name, form.billingCycle)} courses.`
        : "Note : Courses included with this subscription at no extra charge. Limits apply only when the plan name includes the word “Growth”."
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit subscription plan" : "Add subscription plan"}
      size="lg"
      footer={footer}
    >
      <form id="add-plan-form" onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm">
            {error}
          </div>
        )}
        <TextInput
          label="Plan name"
          placeholder="e.g. Growth"
          value={form.name}
          onChange={(e) => handleChange("name", e.target.value)}
          error={touched.name && errors.name}
          required
        />
        <TextInput
          label="Price (numbers only)"
          placeholder="e.g. 999"
          value={form.price}
          onChange={(e) => handleChange("price", e.target.value)}
          error={touched.price && errors.price}
          required
        />
        <Dropdown
          label="Plan type"
          name="billingCycle"
          value={form.billingCycle}
          onChange={(e) => handleChange("billingCycle", e.target.value)}
          options={[
            { value: "monthly", label: "Monthly" },
            { value: "annually", label: "Annually" },
          ]}
          error={touched.billingCycle && errors.billingCycle}
          required
        />
        <TextInput
          label="Tag"
          placeholder="e.g. Best for starters"
          value={form.tag}
          onChange={(e) => handleChange("tag", e.target.value)}
          error={touched.tag && errors.tag}
          required
        />
        <TextInput
          label="Description"
          placeholder="Short description of the plan"
          value={form.description}
          onChange={(e) => handleChange("description", e.target.value)}
          error={touched.description && errors.description}
          required
        />

        <div className="mt-2">
          <Checkbox
            id="isAllAccess"
            label={
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-700">
                  All Access Plan
                </span>
                <p className="text-xs text-gray-500">
                  When enabled, subscribers automatically get access to ALL
                  courses in the database. Individual course selection below
                  will be ignored.
                </p>
              </div>
            }
            checked={form.isAllAccess}
            onChange={(e) =>
              setForm((f) => ({ ...f, isAllAccess: e.target.checked }))
            }
          />
        </div>

        {!form.isAllAccess && (
          <div className="space-y-2">
            {coursesLoadError && (
              <div className="p-2 rounded-lg bg-amber-50 text-amber-900 text-sm">
                {coursesLoadError}
              </div> 
            )}
            <MultiSelect
              label="Free access courses"
              name="freeAccessCourses"
              options={courseOptions}
              getOptionLabel={(option) => option?.title || "Untitled course"}
              value={selectedFreeCourses}
              onChange={onCoursesChange}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              placeholder={
                courseOptions.length
                  ? "Search and select courses"
                  : "No courses in catalogue yet"
              }
              helperText={
                <>
                  <span
                    className={`block ${
                      freeAccessHelper.max != null
                        ? "!text-gray-900 font-bold"
                        : ""
                    }`}
                  >
                    {freeAccessHelper.hint}
                  </span>
                  <span className="block mt-1 font-medium text-gray-700">
                    {courseOptions.length
                      ? `${courseOptions.length} course${
                          courseOptions.length === 1 ? "" : "s"
                        } available · Selected: ${selectedFreeCourses.length}`
                      : "0 courses loaded"}
                    {freeAccessHelper.max != null
                      ? ` / max ${freeAccessHelper.max}`
                      : ""}
                  </span>
                </>
              }
            />
          </div>
        )}

        <TextInput
          label="Features (one per line)"
          placeholder="See all affordable courses&#10;Full access to enrolled courses"
          value={form.featuresText}
          onChange={(e) => handleChange("featuresText", e.target.value)}
          error={touched.featuresText && errors.featuresText}
          multiline
          rows={4}
          required
        />
        <TextInput
          label="Slug (optional, e.g. free, standard, premium — used to match user's current plan)"
          placeholder="e.g. standard"
          value={form.slug}
          onChange={(e) => handleChange("slug", e.target.value)}
          error={touched.slug && errors.slug}
          required
        />
        <TextInput
          label="Sort order (number, lower first)"
          type="number"
          min={0}
          value={form.sortOrder}
          onChange={(e) => handleChange("sortOrder", e.target.value)}
          error={touched.sortOrder && errors.sortOrder}
        />
      </form>
    </Dialog>
  );
};

export default AddPlanModal;
